import { ArrowLeft, Drama, Icon } from "@norican/icons";
import { Button, cn } from "@norican/react";

import { useT } from "@/i18n";
import { useMe } from "@/lib/api";
import { DEMO_ROLE_COLOUR, DEMO_ROLES, type DemoRole } from "@/lib/demo-persona";
import { useDemoPersona } from "@/lib/demo-persona-store";

import { useBackToMe } from "./api";
import { DemoAsMenu } from "./demo-as-menu";

function roleOf(value: string | null | undefined): DemoRole {
  return (DEMO_ROLES as readonly string[]).includes(value ?? "") ? (value as DemoRole) : "other";
}

/**
 * The strip on every page of a demo persona tab (19 Oct): "Demo: viewing as
 * [UAT TEST] Demo Approver (Approver)", in the role's own colour so the room
 * sees who's who, with "Switch persona" and "Back to me".
 *
 * Shown from what the API says (`/auth/me` → `view_as.demo_role`), so it never
 * claims a persona the server is not honouring.
 */
export function DemoPersonaBanner() {
  const { t } = useT();
  const { data: me } = useMe();
  const stored = useDemoPersona();
  const backToMe = useBackToMe();
  const view = me?.view_as ?? null;
  if (stored === null || view === null || !view.demo_role) {
    return null;
  }
  const role = roleOf(view.demo_role);
  return (
    <div
      role="status"
      aria-label={t("demoPersona.label")}
      data-demo-role={role}
      className={cn("w-full text-white", DEMO_ROLE_COLOUR[role])}
    >
      <div className="mx-auto flex w-full max-w-[var(--norican-size-container-xl)] flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 sm:px-6 lg:px-8">
        <Icon icon={Drama} size="md" className="shrink-0" />
        <span className="min-w-0 flex-1 text-sm font-semibold">
          {t("demoPersona.banner", { name: view.name, role: t(`demoPersona.role.${role}`) })}
          <span className="ml-2 font-normal opacity-90">
            {view.can_act ? t("demoPersona.acts") : t("demoPersona.readOnly")}
          </span>
        </span>
        <DemoAsMenu label={t("demoPersona.switch")} onBanner />
        <Button
          size="sm"
          variant="outline"
          tone="neutral"
          className="bg-bg text-fg"
          startIcon={<Icon icon={ArrowLeft} size="sm" />}
          onClick={backToMe}
        >
          {t("demoPersona.backToMe")}
        </Button>
      </div>
    </div>
  );
}
